import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { AlertTriangle, Download, Loader2, RefreshCw } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import {
  checkPortabilityFaxNumberPortsPortabilityCheckPost as checkPortability,
  createPortOrdersFaxNumberPortsPost as createPortOrders,
  deletePortOrderFaxNumberPortsPortOrderIdDelete as deletePortOrder,
  getLoaTemplateFaxNumberPortsPortOrderIdLoaTemplateGet as getLoaTemplate,
  getPortOrderFaxNumberPortsPortOrderIdGet as getPortOrder,
  listPortOrdersFaxNumberPortsGet as listPortOrders,
  submitPortOrderFaxNumberPortsPortOrderIdSubmitPost as submitPortOrder,
  updatePortOrderFaxNumberPortsPortOrderIdPatch as updatePortOrder,
  uploadPortDocumentFaxNumberPortsPortOrderIdDocumentsDocumentKindPost as uploadPortDocument,
} from '../../services/api/sdk.gen';
import type {
  PortabilityResultResponse,
  PortOrderDetail,
  PortOrderDetailsUpdate,
  PortOrderSummary,
  ServiceAddressUpdate,
} from '../../services/api/types.gen';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';
import {
  getBusinessErrorMessage,
  getSurfaceableError,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { formatDateTimeInAgencyZone } from '../../utils/dateUtils';
import { downloadResponseAsFile } from '../../utils/downloadFile';
import { formatPhone } from '../../utils/intake';

const POLL_INTERVAL_MS = 60_000;
const TERMINAL_STATUSES = new Set(['ported', 'cancelled']);
const MAX_NUMBERS = 10;
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  'in-process': 'In process',
  submitted: 'Submitted',
  exception: 'Needs correction',
  'foc-date-confirmed': 'Date confirmed',
  ported: 'Ported',
  cancelled: 'Cancelled',
  'cancel-pending': 'Cancelling',
};

const statusLabel = (status: string): string => STATUS_LABELS[status] ?? status;

type FieldKey =
  | 'entity_name'
  | 'auth_person_name'
  | 'billing'
  | 'pin'
  | 'account'
  | 'address';

// The form field each carrier exception points at, so the person fixing it
// lands on the right input instead of reading codes.
const EXCEPTION_FIELDS: Record<string, FieldKey> = {
  ENTITY_NAME_MISMATCH: 'entity_name',
  AUTH_PERSON_MISMATCH: 'auth_person_name',
  BTN_ATN_MISMATCH: 'billing',
  MAIN_BTN_PORTING: 'billing',
  PASSCODE_PIN_INVALID: 'pin',
  ACCOUNT_NUMBER_MISMATCH: 'account',
  LOCATION_MISMATCH: 'address',
  POSTAL_CODE_MISMATCH: 'address',
};

/** A US number as typed, in the E.164 form the API takes, or null. */
const toE164 = (value: string): string | null => {
  const trimmed = value.trim();
  if (/^\+[1-9][0-9]{7,14}$/.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return null;
};

const displayPhone = (e164: string | null | undefined): string => {
  if (!e164) return '';
  return e164.startsWith('+1') ? formatPhone(e164) : e164;
};

// The porting API flags the 502s and 503s it writes for people as surfaceable.
const describeError = (error: unknown, fallback: string): string =>
  getSurfaceableError(error)?.message ??
  getBusinessErrorMessage(error) ??
  fallback;

// Never the error itself: its request config holds the body, which can carry
// the carrier PIN and account number, and session replay records the console.
const logFailure = (message: string, error: unknown): void => {
  console.error(message, {
    status: getHttpStatus(error),
    code: getSurfaceableError(error)?.errorCode,
  });
};

const SUBMIT_OUTCOME_UNKNOWN = 'submit_outcome_unknown';
const SUBMIT_UNKNOWN_MESSAGE =
  'Submission state unknown. Roger support will check it; do not try again until they do.';

/**
 * Only the server's unknown-outcome code, no answer at all, or an unflagged
 * 5xx can follow a confirm that went out. Any other answer means nothing was
 * sent, and saying otherwise would send the agency to support for nothing.
 */
const describeSubmitError = (error: unknown): string => {
  const surfaced = getSurfaceableError(error);
  if (surfaced) {
    return surfaced.errorCode === SUBMIT_OUTCOME_UNKNOWN
      ? SUBMIT_UNKNOWN_MESSAGE
      : surfaced.message;
  }
  const status = getHttpStatus(error);
  if (status !== undefined && status < 500) {
    return (
      getBusinessErrorMessage(error) ?? 'The port was not submitted. Try again.'
    );
  }
  return SUBMIT_UNKNOWN_MESSAGE;
};

/**
 * Moving the agency's existing fax numbers to Roger through Telnyx.
 *
 * Submitting asks the current carrier to release live numbers and cannot be
 * undone, so it is a separate permission, a typed confirmation and at most one
 * request. The server enforces all three; the screen only makes them obvious.
 */
const FaxNumberPortingSection: React.FC = () => {
  const { showSuccess, showError, hasPermission } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
      hasPermission: state.hasPermission,
    }))
  );
  const canSend = hasPermission(PermissionSlug.FAX_NUMBER_PORTS_SEND);

  const [loadState, setLoadState] = useState<
    'loading' | 'ready' | 'not-configured' | 'error'
  >('loading');
  const [orders, setOrders] = useState<PortOrderSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<PortOrderDetail | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [detailFailed, setDetailFailed] = useState(false);
  const selectedIdRef = useRef<string | null>(null);
  selectedIdRef.current = selectedId;
  // Bumped by every load and every applied change: only the newest may land,
  // so a slow answer can never paint over what came after it.
  const versionRef = useRef(0);

  const loadDetail = useCallback(
    async (id: string, { quiet = false }: { quiet?: boolean } = {}) => {
      const version = ++versionRef.current;
      setIsRefreshing(true);
      try {
        const { data } = await getPortOrder({
          path: { port_order_id: id },
          throwOnError: true,
        });
        // Also dropped: a slow answer for an order the user has since left.
        if (versionRef.current !== version || selectedIdRef.current !== id) {
          return;
        }
        setDetail(data);
        setDetailFailed(false);
        setOrders(current =>
          current.map(order => (order.id === data.id ? data : order))
        );
      } catch (error) {
        logFailure('Failed to load fax number port', error);
        if (!quiet && versionRef.current === version) {
          setDetailFailed(true);
          showError(
            describeError(error, 'Unable to load this port. Please try again.')
          );
        }
      } finally {
        if (versionRef.current === version) setIsRefreshing(false);
      }
    },
    [showError]
  );

  // A save, upload or submit answers from Telnyx's update, which is not the
  // full order; a quiet read straight after makes the screen match a Refresh.
  const applyChange = useCallback(
    (data: PortOrderDetail) => {
      setOrders(current =>
        current.map(order => (order.id === data.id ? data : order))
      );
      // The user may have opened another order while this one was saving.
      if (selectedIdRef.current !== data.id) return;
      versionRef.current += 1;
      setDetail(data);
      void loadDetail(data.id, { quiet: true });
    },
    [loadDetail]
  );

  const openOrder = useCallback(
    (id: string) => {
      setSelectedId(id);
      selectedIdRef.current = id;
      setDetail(null);
      setDetailFailed(false);
      void loadDetail(id);
    },
    [loadDetail]
  );

  const loadOrders = useCallback(async (): Promise<PortOrderSummary[]> => {
    try {
      const { data } = await listPortOrders({ throwOnError: true });
      setOrders(data);
      setLoadState('ready');
      return data;
    } catch (error) {
      logFailure('Failed to load fax number ports', error);
      setLoadState(getHttpStatus(error) === 503 ? 'not-configured' : 'error');
      return [];
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void loadOrders().then(list => {
      const open = list.find(order => order.status !== 'cancelled');
      if (!cancelled && open) openOrder(open.id);
    });
    return () => {
      cancelled = true;
    };
  }, [loadOrders, openOrder]);

  const detailStatus = detail?.status;
  useEffect(() => {
    if (!selectedId || !detailStatus || TERMINAL_STATUSES.has(detailStatus)) {
      return;
    }
    const timer = window.setInterval(() => {
      // Nobody is looking at a hidden tab; the next visible tick catches up.
      if (document.hidden) return;
      void loadDetail(selectedId, { quiet: true });
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [selectedId, detailStatus, loadDetail]);

  const handleCreated = async (created: PortOrderSummary[]) => {
    await loadOrders();
    if (created[0]) openOrder(created[0].id);
  };

  const handleDeleted = async () => {
    setSelectedId(null);
    selectedIdRef.current = null;
    setDetail(null);
    await loadOrders();
  };

  if (loadState === 'loading') {
    return (
      <div className='flex items-center gap-2 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' />
        Loading fax number ports...
      </div>
    );
  }

  if (loadState === 'not-configured') {
    return (
      <Alert>
        <AlertDescription>
          Number porting is not configured. Contact Roger support.
        </AlertDescription>
      </Alert>
    );
  }

  if (loadState === 'error') {
    return (
      <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
        <p className='text-sm text-muted-foreground'>
          Unable to load fax number ports.
        </p>
        <Button variant='outline' size='sm' onClick={() => void loadOrders()}>
          Try again
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-8'>
      <p className='text-sm text-muted-foreground'>
        Move your existing fax numbers to Roger.
      </p>

      <PortabilityCheck
        onCreated={handleCreated}
        showSuccess={showSuccess}
        showError={showError}
      />

      {/* Marked open only once loaded, so a failed load can be reopened. */}
      {orders.length > 0 && (
        <OrderList
          orders={orders}
          selectedId={detail?.id ?? null}
          onOpen={openOrder}
        />
      )}

      {selectedId && !detail && detailFailed && (
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
          <p className='text-sm text-muted-foreground'>
            Unable to load this port.
          </p>
          <Button
            variant='outline'
            size='sm'
            onClick={() => openOrder(selectedId)}
          >
            Try again
          </Button>
        </div>
      )}

      {selectedId && !detail && !detailFailed && (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <Loader2 className='h-4 w-4 animate-spin' />
          Loading port...
        </div>
      )}

      {detail && (
        <OrderDetail
          key={detail.id}
          detail={detail}
          canSend={canSend}
          isRefreshing={isRefreshing}
          onRefresh={() => void loadDetail(detail.id)}
          onChanged={applyChange}
          onDeleted={handleDeleted}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}
    </div>
  );
};

type Notify = (message: string) => void;

// ---------------------------------------------------------------------------
// Portability check and start
// ---------------------------------------------------------------------------

const PortabilityCheck: React.FC<{
  onCreated: (orders: PortOrderSummary[]) => Promise<void>;
  showSuccess: Notify;
  showError: Notify;
}> = ({ onCreated, showSuccess, showError }) => {
  const [numbersText, setNumbersText] = useState('');
  const [results, setResults] = useState<PortabilityResultResponse[] | null>(
    null
  );
  const [isChecking, setIsChecking] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // Results belong to the text that was checked: editing it or starting a
  // port drops them, and a check answered after that is ignored, so Start
  // port never sends a number the user has since replaced.
  const checkRef = useRef(0);
  const invalidateCheck = () => {
    checkRef.current += 1;
    setResults(null);
    setIsChecking(false);
  };

  const handleCheck = async () => {
    if (isChecking || isCreating) return;
    const entries = numbersText
      .split(/[,;\n]/)
      .map(entry => entry.trim())
      .filter(Boolean);
    const numbers = entries.map(toE164);
    if (entries.length === 0 || numbers.some(number => number === null)) {
      showError('Enter 10-digit phone numbers, separated by commas.');
      return;
    }
    if (numbers.length > MAX_NUMBERS) {
      showError(`Check at most ${MAX_NUMBERS} numbers at a time.`);
      return;
    }
    const check = ++checkRef.current;
    setIsChecking(true);
    setResults(null);
    try {
      const { data } = await checkPortability({
        body: { phone_numbers: [...new Set(numbers as string[])] },
        throwOnError: true,
      });
      if (check === checkRef.current) setResults(data);
    } catch (error) {
      if (check !== checkRef.current) return;
      logFailure('Portability check failed', error);
      showError(describeError(error, 'Unable to check these numbers.'));
    } finally {
      if (check === checkRef.current) setIsChecking(false);
    }
  };

  const portable = (results ?? [])
    .filter(result => result.portable)
    .map(result => result.phone_number);

  const handleStart = async () => {
    setIsCreating(true);
    try {
      const { data } = await createPortOrders({
        body: { phone_numbers: portable },
        throwOnError: true,
      });
      showSuccess('Port started');
      invalidateCheck();
      setNumbersText('');
      await onCreated(data);
    } catch (error) {
      logFailure('Failed to start fax number port', error);
      showError(describeError(error, 'Unable to start the port.'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <section className='space-y-3'>
      <h3 className='text-base font-semibold'>Check numbers</h3>
      <div className='flex flex-col gap-2 sm:flex-row sm:items-end'>
        <div className='grid w-full gap-1.5 sm:max-w-md'>
          <Label htmlFor='port-check-numbers'>Fax numbers to move</Label>
          <Input
            id='port-check-numbers'
            placeholder='415-555-0100, 415-555-0101'
            value={numbersText}
            disabled={isCreating}
            onChange={event => {
              invalidateCheck();
              setNumbersText(event.target.value);
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') void handleCheck();
            }}
          />
        </div>
        <Button
          variant='outline'
          onClick={() => void handleCheck()}
          disabled={isChecking || isCreating || !numbersText.trim()}
        >
          {isChecking && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Check
        </Button>
      </div>

      {results && (
        <div className='space-y-3'>
          <div className='overflow-x-auto rounded-md border'>
            <Table className='min-w-[420px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Can move</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map(result => (
                  <TableRow key={result.phone_number}>
                    <TableCell className='whitespace-nowrap'>
                      {displayPhone(result.phone_number)}
                    </TableCell>
                    <TableCell>
                      {result.portable === true
                        ? 'Yes'
                        : result.portable === false
                          ? 'No'
                          : 'Unknown'}
                    </TableCell>
                    <TableCell className='text-muted-foreground'>
                      {result.not_portable_reason ?? ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Button
            onClick={() => void handleStart()}
            disabled={isCreating || portable.length === 0}
          >
            {isCreating && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Start port
          </Button>
        </div>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Orders
// ---------------------------------------------------------------------------

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <Badge
    variant={
      status === 'exception'
        ? 'destructive'
        : status === 'draft' || status === 'cancelled'
          ? 'secondary'
          : 'default'
    }
    className='whitespace-nowrap'
  >
    {statusLabel(status)}
  </Badge>
);

const OrderList: React.FC<{
  orders: PortOrderSummary[];
  selectedId: string | null;
  onOpen: (id: string) => void;
}> = ({ orders, selectedId, onOpen }) => (
  <section className='space-y-3'>
    <h3 className='text-base font-semibold'>Ports</h3>
    <div className='overflow-x-auto rounded-md border'>
      <Table className='min-w-[480px]'>
        <TableHeader>
          <TableRow>
            <TableHead>Numbers</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Started</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {orders.map(order => (
            <TableRow key={order.id}>
              <TableCell>
                {order.phone_numbers.map(displayPhone).join(', ')}
              </TableCell>
              <TableCell>
                <StatusBadge status={order.status} />
              </TableCell>
              <TableCell className='whitespace-nowrap'>
                {formatDateTimeInAgencyZone(order.created_at)}
              </TableCell>
              <TableCell className='text-right'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => onOpen(order.id)}
                  disabled={order.id === selectedId}
                >
                  {order.id === selectedId ? 'Open' : 'View'}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </section>
);

const OrderDetail: React.FC<{
  detail: PortOrderDetail;
  canSend: boolean;
  isRefreshing: boolean;
  onRefresh: () => void;
  onChanged: (detail: PortOrderDetail) => void;
  onDeleted: () => Promise<void>;
  showSuccess: Notify;
  showError: Notify;
}> = ({
  detail,
  canSend,
  isRefreshing,
  onRefresh,
  onChanged,
  onDeleted,
  showSuccess,
  showError,
}) => {
  const [isSubmitOpen, setIsSubmitOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const editable = detail.status === 'draft' && !detail.submit_in_flight;
  const flagged = new Set(
    detail.exception_codes
      .map(code => EXCEPTION_FIELDS[code])
      .filter((field): field is FieldKey => Boolean(field))
  );
  const readyToSubmit = editable && detail.missing_for_submit.length === 0;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deletePortOrder({
        path: { port_order_id: detail.id },
        throwOnError: true,
      });
      showSuccess('Draft deleted');
      setIsDeleteOpen(false);
      await onDeleted();
    } catch (error) {
      logFailure('Failed to delete fax number port', error);
      showError(describeError(error, 'Unable to delete the draft.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <section className='space-y-6 rounded-md border p-4'>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div className='min-w-0 space-y-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <h3 className='text-base font-semibold'>
              {detail.phone_numbers.map(displayPhone).join(', ')}
            </h3>
            <StatusBadge status={detail.status} />
          </div>
          <dl className='grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-[auto_1fr]'>
            <dt className='text-muted-foreground'>Current carrier</dt>
            <dd>{detail.losing_carrier_ocn ?? 'Not known yet'}</dd>
            <dt className='text-muted-foreground'>Port date</dt>
            <dd>
              {detail.foc_at
                ? formatDateTimeInAgencyZone(detail.foc_at)
                : 'Not scheduled'}
            </dd>
          </dl>
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={onRefresh}
          disabled={isRefreshing}
        >
          {isRefreshing ? (
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
          ) : (
            <RefreshCw className='mr-2 h-4 w-4' />
          )}
          Refresh
        </Button>
      </div>

      {detail.submit_in_flight && (
        <Alert>
          <AlertDescription>
            Submission may be in progress. If this still says draft in a few
            minutes, contact Roger support.
          </AlertDescription>
        </Alert>
      )}
      {detail.status === 'exception' && (
        <Alert variant='destructive'>
          <AlertDescription className='space-y-2'>
            <p>Contact Roger support to correct and resubmit.</p>
            <ul className='list-disc space-y-1 pl-5'>
              {detail.exceptions.map(exception => (
                <li key={exception.code}>
                  {exception.description ?? exception.code}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      {detail.status === 'ported' && (
        <Alert>
          <AlertDescription>
            Roger connects this number to your fax inbox on its own once Roger
            staff have set up your Telnyx fax line. Contact Roger support if
            faxes to it do not arrive.
          </AlertDescription>
        </Alert>
      )}

      <PortDetailsForm
        detail={detail}
        editable={editable}
        flagged={flagged}
        onSaved={onChanged}
        showSuccess={showSuccess}
        showError={showError}
      />

      <PortDocuments
        detail={detail}
        editable={editable}
        onUploaded={onChanged}
        showSuccess={showSuccess}
        showError={showError}
      />

      {detail.status === 'draft' && (
        <section className='space-y-2'>
          <h4 className='text-sm font-semibold'>Before you submit</h4>
          {detail.missing_for_submit.length === 0 ? (
            <p className='text-sm text-muted-foreground'>Ready to submit.</p>
          ) : (
            <ul
              aria-label='Still needed'
              className='list-disc space-y-1 pl-5 text-sm'
            >
              {detail.missing_for_submit.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      {detail.comments.length > 0 && (
        <section className='space-y-2'>
          <h4 className='text-sm font-semibold'>Messages</h4>
          <ul className='space-y-2 text-sm'>
            {detail.comments.map((comment, index) => (
              <li key={`${comment.created_at ?? ''}-${index}`}>
                <p>{comment.body}</p>
                {comment.created_at && (
                  <p className='text-xs text-muted-foreground'>
                    {formatDateTimeInAgencyZone(comment.created_at)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {editable && (
        <div className='flex flex-wrap gap-2'>
          {canSend && (
            <Button
              onClick={() => setIsSubmitOpen(true)}
              disabled={!readyToSubmit}
            >
              Submit port
            </Button>
          )}
          <Button variant='outline' onClick={() => setIsDeleteOpen(true)}>
            Delete draft
          </Button>
        </div>
      )}

      {isSubmitOpen && (
        <SubmitDialog
          detail={detail}
          onClose={() => setIsSubmitOpen(false)}
          onSubmitted={onChanged}
          onFailed={onRefresh}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}

      <ConfirmationDialog
        open={isDeleteOpen}
        title='Delete this draft?'
        message='The draft is deleted. Your numbers stay with your current carrier.'
        confirmLabel='Delete draft'
        destructive
        loading={isDeleting}
        onAccept={() => void handleDelete()}
        onCancel={() => setIsDeleteOpen(false)}
      />
    </section>
  );
};

// ---------------------------------------------------------------------------
// Details form
// ---------------------------------------------------------------------------

interface FormValues {
  entity_name: string;
  auth_person_name: string;
  billing: string;
  newBilling: string;
  pin: string;
  account: string;
  street_address: string;
  extended_address: string;
  locality: string;
  administrative_area: string;
  postal_code: string;
  country_code: string;
}

// The billing number may be one being ported; the one that stays never may.
const isPorted = (
  detail: PortOrderDetail,
  e164: string | null | undefined
): boolean => e164 != null && detail.phone_numbers.includes(e164);

const initialValues = (detail: PortOrderDetail): FormValues => ({
  entity_name: detail.entity_name ?? '',
  auth_person_name: detail.auth_person_name ?? '',
  billing: displayPhone(detail.billing_phone_number),
  newBilling: displayPhone(
    detail.new_billing_phone_number ??
      (isPorted(detail, detail.billing_phone_number)
        ? null
        : detail.billing_phone_number)
  ),
  // Never prefilled: the carrier PIN and account number are write-only.
  pin: '',
  account: '',
  street_address: detail.service_address.street_address ?? '',
  extended_address: detail.service_address.extended_address ?? '',
  locality: detail.service_address.locality ?? '',
  administrative_area: detail.service_address.administrative_area ?? '',
  postal_code: detail.service_address.postal_code ?? '',
  country_code: detail.service_address.country_code ?? 'US',
});

const ADDRESS_FIELDS = [
  'street_address',
  'extended_address',
  'locality',
  'administrative_area',
  'postal_code',
  'country_code',
] as const;

const ADDRESS_LABELS: Record<(typeof ADDRESS_FIELDS)[number], string> = {
  street_address: 'Street',
  extended_address: 'Suite or unit',
  locality: 'City',
  administrative_area: 'State',
  postal_code: 'ZIP code',
  country_code: 'Country',
};

/**
 * Only what changed is sent: the server reads a missing key as "keep", and
 * the PIN and account number are sent only when typed, so a blank one never
 * overwrites what the carrier already has.
 */
const buildDetailsUpdate = (
  detail: PortOrderDetail,
  values: FormValues
): PortOrderDetailsUpdate | string => {
  const body: PortOrderDetailsUpdate = {};
  const entity = values.entity_name.trim();
  if (entity && entity !== (detail.entity_name ?? ''))
    body.entity_name = entity;
  const signer = values.auth_person_name.trim();
  if (signer && signer !== (detail.auth_person_name ?? '')) {
    body.auth_person_name = signer;
  }
  if (values.billing.trim()) {
    const billing = toE164(values.billing);
    if (!billing) return 'Enter the billing number as a 10-digit phone number.';
    if (billing !== detail.billing_phone_number) {
      body.billing_phone_number = billing;
    }
    if (isPorted(detail, billing) && !values.newBilling.trim()) {
      return 'Your billing number is being ported. Also enter a number on the account that stays with your current carrier.';
    }
  }
  if (values.newBilling.trim()) {
    const newBilling = toE164(values.newBilling);
    if (!newBilling) {
      return 'Enter the number that stays with your current carrier as a 10-digit phone number.';
    }
    if (isPorted(detail, newBilling)) {
      return 'The number that stays with your current carrier cannot be one of the numbers being ported.';
    }
    if (newBilling !== detail.new_billing_phone_number) {
      body.new_billing_phone_number = newBilling;
    }
  }
  if (values.pin.trim()) body.pin_passcode = values.pin.trim();
  if (values.account.trim()) body.account_number = values.account.trim();
  const address: ServiceAddressUpdate = {};
  for (const field of ADDRESS_FIELDS) {
    const value = values[field].trim();
    // An emptied line is sent as null, which clears it at Telnyx.
    if (value !== (detail.service_address[field] ?? '')) {
      address[field] = value || null;
    }
  }
  if (Object.keys(address).length > 0) body.service_address = address;
  return body;
};

const PortDetailsForm: React.FC<{
  detail: PortOrderDetail;
  editable: boolean;
  flagged: Set<FieldKey>;
  onSaved: (detail: PortOrderDetail) => void;
  showSuccess: Notify;
  showError: Notify;
}> = ({ detail, editable, flagged, onSaved, showSuccess, showError }) => {
  const [values, setValues] = useState<FormValues>(() => initialValues(detail));
  // Until someone types into it, the remaining billing number follows the
  // billing number, unless that is a number being ported.
  const [newBillingTouched, setNewBillingTouched] = useState(
    Boolean(detail.new_billing_phone_number)
  );
  const [isSaving, setIsSaving] = useState(false);

  // Each field follows the order as it refreshes until someone types in it, so
  // a refresh never leaves an untouched field stale for a save to send back.
  const typedRef = useRef(new Set<keyof FormValues>());
  useEffect(() => {
    const next = initialValues(detail);
    const typed = typedRef.current;
    setValues(current => {
      const merged = { ...next };
      for (const key of typed) merged[key] = current[key];
      return merged;
    });
    if (!typed.has('newBilling')) {
      setNewBillingTouched(Boolean(detail.new_billing_phone_number));
    }
  }, [detail]);

  const set =
    (field: keyof FormValues) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const follows = field === 'billing' && !newBillingTouched;
      typedRef.current.add(field);
      if (follows) typedRef.current.add('newBilling');
      setValues(current => ({
        ...current,
        [field]: value,
        ...(follows
          ? { newBilling: isPorted(detail, toE164(value)) ? '' : value }
          : {}),
      }));
      if (field === 'newBilling') setNewBillingTouched(true);
    };

  const billingIsPorted = isPorted(detail, toE164(values.billing));
  const pending = buildDetailsUpdate(detail, values);
  const hasChanges =
    typeof pending === 'string' || Object.keys(pending).length > 0;

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (typeof pending === 'string') {
      showError(pending);
      return;
    }
    setIsSaving(true);
    try {
      const { data } = await updatePortOrder({
        path: { port_order_id: detail.id },
        body: pending,
        throwOnError: true,
      });
      showSuccess('Port details saved');
      // Saved, so every field follows the order again.
      typedRef.current.clear();
      setValues(current => ({ ...current, pin: '', account: '' }));
      onSaved(data);
    } catch (error) {
      logFailure('Failed to save fax number port details', error);
      showError(describeError(error, 'Unable to save the port details.'));
    } finally {
      setIsSaving(false);
    }
  };

  const field = (
    key: keyof FormValues,
    label: string,
    options: {
      flag?: FieldKey;
      onFile?: boolean;
      type?: string;
      className?: string;
      hint?: string;
    } = {}
  ) => {
    const id = `port-${key}`;
    const hintId = `${id}-hint`;
    const isFlagged = options.flag ? flagged.has(options.flag) : false;
    // A saved value reads as filled, but the dots are only a placeholder: the
    // value stays empty, so an untouched save sends nothing. The input stays
    // plain text, so a replacement PIN or account number is typed in view.
    const hint = options.onFile ? 'Saved. Type to replace.' : options.hint;
    return (
      <div className={`grid gap-1.5 ${options.className ?? ''}`}>
        <Label htmlFor={id} className={isFlagged ? 'text-destructive' : ''}>
          {label}
          {options.onFile && (
            <span className='ml-2 font-normal text-muted-foreground'>
              On file
            </span>
          )}
        </Label>
        <Input
          id={id}
          type={options.type ?? 'text'}
          autoComplete='off'
          value={values[key]}
          onChange={set(key)}
          disabled={!editable || isSaving}
          aria-invalid={isFlagged ? 'true' : undefined}
          className={isFlagged ? 'border-destructive' : undefined}
          placeholder={options.onFile ? '••••••••' : undefined}
          aria-describedby={hint ? hintId : undefined}
        />
        {hint && (
          <p id={hintId} className='text-xs text-muted-foreground'>
            {hint}
          </p>
        )}
      </div>
    );
  };

  return (
    <form className='space-y-4' onSubmit={event => void handleSave(event)}>
      <h4 className='text-sm font-semibold'>
        Account with your current carrier
      </h4>
      <div className='grid grid-cols-1 items-start gap-4 sm:grid-cols-2'>
        {field('entity_name', 'Legal business name', { flag: 'entity_name' })}
        {field('auth_person_name', 'Authorized signer', {
          flag: 'auth_person_name',
        })}
        {field('billing', 'Billing number', {
          flag: 'billing',
          type: 'tel',
          hint: 'The main number on your carrier account. It can be one of the numbers you are porting.',
        })}
        {field(
          'newBilling',
          'Billing number that stays with your current carrier',
          {
            flag: 'billing',
            type: 'tel',
            hint: `${
              billingIsPorted
                ? 'Your billing number is being ported, so enter another number on the account that stays with your current carrier.'
                : 'Must not be one of the numbers you are porting.'
            } If every number on your carrier account is being moved, contact Roger support instead.`,
          }
        )}
        {field('account', 'Account number', {
          flag: 'account',
          onFile: detail.account_number_on_file,
        })}
        {field('pin', 'Port-out PIN', {
          flag: 'pin',
          onFile: detail.pin_on_file,
        })}
      </div>

      <h4 className='text-sm font-semibold'>
        Service address on file with your current carrier
      </h4>
      <div className='grid grid-cols-1 items-start gap-4 sm:grid-cols-2'>
        {ADDRESS_FIELDS.map(key => (
          <React.Fragment key={key}>
            {field(key, ADDRESS_LABELS[key], {
              flag: 'address',
              className: key === 'street_address' ? 'sm:col-span-2' : '',
            })}
          </React.Fragment>
        ))}
      </div>

      {editable && (
        <Button type='submit' disabled={isSaving || !hasChanges}>
          {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
          Save details
        </Button>
      )}
    </form>
  );
};

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

const DOCUMENT_KINDS = [
  { kind: 'loa', label: 'Signed letter of authorization' },
  { kind: 'invoice', label: 'Recent invoice from your current carrier' },
] as const;

const PortDocuments: React.FC<{
  detail: PortOrderDetail;
  editable: boolean;
  onUploaded: (detail: PortOrderDetail) => void;
  showSuccess: Notify;
  showError: Notify;
}> = ({ detail, editable, onUploaded, showSuccess, showError }) => {
  const [uploading, setUploading] = useState<'loa' | 'invoice' | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await getLoaTemplate({
        path: { port_order_id: detail.id },
        throwOnError: true,
      });
      downloadResponseAsFile(response, 'loa-template.pdf');
    } catch (error) {
      logFailure('Failed to download LOA template', error);
      showError(describeError(error, 'Unable to download the letter.'));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUpload = async (kind: 'loa' | 'invoice', file: File) => {
    if (file.type !== 'application/pdf') {
      showError('Upload a PDF.');
      return;
    }
    if (file.size > MAX_DOCUMENT_BYTES) {
      showError('The file must be 10 MB or smaller.');
      return;
    }
    setUploading(kind);
    try {
      const { data } = await uploadPortDocument({
        path: { port_order_id: detail.id, document_kind: kind },
        body: { file },
        throwOnError: true,
      });
      showSuccess('Document attached');
      onUploaded(data);
    } catch (error) {
      logFailure('Failed to upload port document', error);
      showError(describeError(error, 'Unable to attach the document.'));
    } finally {
      setUploading(null);
    }
  };

  const attached = {
    loa: detail.documents.loa_attached,
    invoice: detail.documents.invoice_attached,
  };

  return (
    <section className='space-y-3'>
      <h4 className='text-sm font-semibold'>Documents</h4>
      <Button
        variant='outline'
        size='sm'
        onClick={() => void handleDownload()}
        disabled={isDownloading}
      >
        {isDownloading ? (
          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
        ) : (
          <Download className='mr-2 h-4 w-4' />
        )}
        Letter of authorization to sign
      </Button>
      <ul className='space-y-3'>
        {DOCUMENT_KINDS.map(({ kind, label }) => (
          <li
            key={kind}
            className='flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between'
          >
            <span>
              {label}
              <span className='ml-2 text-muted-foreground'>
                {attached[kind] ? 'Attached' : 'Not attached'}
              </span>
            </span>
            {editable && (
              <label className='flex items-center gap-2'>
                {uploading === kind && (
                  <Loader2 className='h-4 w-4 animate-spin' />
                )}
                <input
                  type='file'
                  accept='application/pdf'
                  aria-label={`Upload ${label}`}
                  disabled={uploading !== null}
                  className='w-full text-sm sm:w-auto'
                  onChange={event => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    if (file) void handleUpload(kind, file);
                  }}
                />
              </label>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Submit
// ---------------------------------------------------------------------------

const SubmitDialog: React.FC<{
  detail: PortOrderDetail;
  onClose: () => void;
  onSubmitted: (detail: PortOrderDetail) => void;
  onFailed: () => void;
  showSuccess: Notify;
  showError: Notify;
}> = ({ detail, onClose, onSubmitted, onFailed, showSuccess, showError }) => {
  const [typed, setTyped] = useState<string[]>(() =>
    detail.phone_numbers.map(() => '')
  );
  const [clicked, setClicked] = useState(false);
  // A ref as well as state: a second click can land before the re-render.
  const clickedRef = useRef(false);

  // With no numbers listed, every() is vacuously true; nothing to confirm.
  const allMatch =
    detail.phone_numbers.length > 0 &&
    detail.phone_numbers.every(
      (number, index) => toE164(typed[index] ?? '') === number
    );

  const handleConfirm = async () => {
    if (clickedRef.current || !allMatch) return;
    clickedRef.current = true;
    setClicked(true);
    try {
      const { data } = await submitPortOrder({
        path: { port_order_id: detail.id },
        body: { confirm_phone_numbers: detail.phone_numbers },
        throwOnError: true,
      });
      showSuccess('Port submitted');
      onSubmitted(data);
    } catch (error) {
      logFailure('Failed to submit fax number port', error);
      showError(describeSubmitError(error));
      onFailed();
    } finally {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && !clicked && onClose()}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle>Submit port</DialogTitle>
          <DialogDescription>
            Your current carrier is asked to move these numbers to Roger. This
            cannot be undone. Type each number to confirm.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-2'>
          {detail.phone_numbers.length === 0 && (
            <p className='text-sm text-destructive'>
              This order does not list its numbers yet. Refresh it before
              submitting.
            </p>
          )}
          {detail.phone_numbers.map((number, index) => (
            <div key={number} className='grid gap-1.5'>
              <Label htmlFor={`port-confirm-${index}`}>
                Type {displayPhone(number)}
              </Label>
              <Input
                id={`port-confirm-${index}`}
                type='tel'
                autoComplete='off'
                value={typed[index] ?? ''}
                disabled={clicked}
                onChange={event => {
                  const value = event.target.value;
                  setTyped(current =>
                    current.map((entry, i) => (i === index ? value : entry))
                  );
                }}
              />
            </div>
          ))}
          <p className='flex items-start gap-2 text-sm text-muted-foreground'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
            Faxes to these numbers stop arriving at your current carrier on the
            port date.
          </p>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={clicked}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            disabled={!allMatch || clicked}
          >
            {clicked && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FaxNumberPortingSection;
